/**
 * useEingangAudit - the Eingang's audit rows, for Teil 5 beside the Aktivitätenhistory.
 *
 * Two lists, not one: `activity` is the work log anyone who may see the Eingang can
 * read; `audit_log` is the append-only compliance record, gated to `audit.read`.
 * Merging them would blur which entries are evidence.
 *
 * An Eingang's trail spans the communication AND every task it produced, and GET /audit
 * filters one entity_id, so this fires one query per entity and merges newest-first.
 * `GET /audit/object/:type/:id` would be better, but its DTO is not in packages/shared.
 *
 * [Source: docs/eingangsverarbeitung-epic.md §3 WS-C C5, WS-J J1]
 */

import { useQueries } from '@tanstack/react-query';
import type { AuditEntry, AuditListResponse } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { qk } from '../../../lib/query-keys';
import { eingangAuditMock } from '../mock';

/** How many rows to pull per entity. T5 is a panel, not the admin log's paged table. */
const PER_ENTITY_LIMIT = 25;

export interface EingangAuditResult {
  rows: AuditEntry[];
  isLoading: boolean;
  /** True when at least one entity's query failed - the list is then incomplete. */
  isError: boolean;
}

/**
 * The audit rows of one Eingang and its tasks, newest first.
 *
 * `enabled` is the caller's gate: T5 passes `perms.can('audit.read') && panel open`, so a
 * broker without the permission never fires a request that would 403.
 */
export function useEingangAudit(
  commId: string | null,
  taskIds: string[],
  enabled: boolean,
): EingangAuditResult {
  const live = isLiveData();
  // The communication first, then its tasks — a stable order so the query keys are
  // stable across renders (task order comes from the list, which does not reshuffle).
  const entities: Array<{ type: string; id: string }> = [
    ...(commId ? [{ type: 'communication', id: commId }] : []),
    ...taskIds.map((id) => ({ type: 'task', id })),
  ];

  const results = useQueries({
    queries: entities.map((entity) => ({
      queryKey: qk.eingangAudit(entity.type, entity.id, live ? 'live' : 'mock'),
      queryFn: (): Promise<AuditListResponse> =>
        live
          ? apiClient.admin.audit({
              entity_type: entity.type,
              entity_id: entity.id,
              limit: PER_ENTITY_LIMIT,
            })
          : Promise.resolve({
              data: eingangAuditMock(entity.id),
              page: { next_cursor: null, limit: PER_ENTITY_LIMIT },
            }),
      enabled,
      // WORM: an audit row never changes once written, so a fetched page is final.
      staleTime: Infinity,
      retry: false,
    })),
  });

  const rows = results
    .flatMap((r) => r.data?.data ?? [])
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));

  return {
    rows,
    isLoading: enabled && results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
  };
}
